import React, { FC, useContext, useState, PropsWithChildren } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import Swal from 'sweetalert2'
import { AuthContext } from 'src/context/auth/authContext'
import { logoutSwalConfig } from 'src/utils/swal.utils'
import Icon from 'src/components/Icons/Icon'
import { ReactComponent as MetaMark } from 'src/assets/icons/general/MetaphysioMark.svg'
import 'src/styles/design-system.css'
import AssistantChatButton from 'src/components/AssistantChatButton/AssistantChatButton'

interface NavLink {
  key: string
  text: string
  icon: Parameters<typeof Icon>[0]['name']
  link: string
  roles: string[]
  count?: number
}

const NAV_GENERAL: NavLink[] = [
  { key: 'citas', text: 'Citas', icon: 'calendar', link: '/citas', roles: ['role1','role2','role3','role4','role5','role6'] },
  { key: 'companias', text: 'Compañías', icon: 'building', link: '/companias', roles: ['role1'] },
  { key: 'tiendas', text: 'Almacenes', icon: 'warehouse', link: '/tiendas', roles: ['role1'] },
  { key: 'usuarios', text: 'Usuarios', icon: 'users', link: '/usuarios', roles: ['role1','role3','role4','role5'] },
  { key: 'campanas', text: 'Campañas', icon: 'megaphone', link: '/campanas', roles: ['role1','role3','role4','role5'] },
  { key: 'incidentes', text: 'Incidentes', icon: 'alert', link: '/incidentes', roles: ['role1','role3','role4','role5','role6'] },
  { key: 'calendarios', text: 'Calendarios', icon: 'calendar', link: '/calendarios', roles: ['role1','role2'] },
  { key: 'estadisticas', text: 'Estadísticas', icon: 'stats', link: '/estadisticas', roles: ['role1','role3','role4'] },
  { key: 'utilidades', text: 'Utilidades', icon: 'tools', link: '/utilidades', roles: ['role1','role2','role3','role4','role5','role6'] },
  { key: 'ayuda', text: 'Ayuda', icon: 'help', link: '/ayuda', roles: ['role1','role2','role3','role4','role5','role6'] },
]

const NAV_RRHH: NavLink[] = [
  { key: 'areas', text: 'Áreas', icon: 'area', link: '/areas', roles: ['role1'] },
  { key: 'patologias', text: 'Patologías', icon: 'body', link: '/patologias', roles: ['role1'] },
  { key: 'procesos', text: 'Procesos', icon: 'workflow', link: '/procesos', roles: ['role1'] },
  { key: 'sectores', text: 'Sectores', icon: 'sector', link: '/sectores', roles: ['role1'] },
  { key: 'maquinas', text: 'Máquinas', icon: 'machine', link: '/maquinaria', roles: ['role1', 'role3'] },
  { key: 'puestos', text: 'Puestos', icon: 'briefcase', link: '/puestos', roles: ['role1', 'role3'] },
]

function getInitials(name: string): string {
  return name
    .split(' ')
    .slice(0, 2)
    .map((w) => w[0] || '')
    .join('')
    .toUpperCase()
}

interface SidebarProps {
  activeKey: string
  role: string
  userName: string
  userRole: string
  onLogout: () => void
}

function Sidebar({ activeKey, role, userName, userRole, onLogout }: SidebarProps) {
  const history = useHistory()

  const visibleGeneral = NAV_GENERAL.filter((l) => l.roles.includes(role))
  const visibleRrhh = NAV_RRHH.filter((l) => l.roles.includes(role))

  return (
    <aside className="ds-sidebar">
      <div className="ds-sidebar-header">
        <div className="ds-logo">
          <MetaMark className="ds-logo-mark" />
          METAPHYSIO
        </div>
      </div>

      <div style={{ overflowY: 'auto', flex: 1 }}>
        <div className="ds-nav-section">
          <div className="ds-nav-title">General</div>
          {visibleGeneral.map((item) => (
            <div
              key={item.key}
              className={`ds-nav-item${activeKey === item.key ? ' ds-active' : ''}`}
              onClick={() => history.push(item.link)}
            >
              <span className="ds-nav-ico"><Icon name={item.icon} size={17} /></span>
              <span>{item.text}</span>
              {item.count ? <span className="ds-nav-count">{item.count}</span> : null}
            </div>
          ))}
        </div>

        {visibleRrhh.length > 0 && (
          <div className="ds-nav-section">
            <div className="ds-nav-title">RRHH</div>
            {visibleRrhh.map((item) => (
              <div
                key={item.key}
                className={`ds-nav-item${activeKey === item.key ? ' ds-active' : ''}`}
                onClick={() => history.push(item.link)}
              >
                <span className="ds-nav-ico"><Icon name={item.icon} size={17} /></span>
                <span>{item.text}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="ds-sidebar-footer">
        <div className="ds-avatar">{getInitials(userName)}</div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="ds-user-name" style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{userName}</div>
          <div className="ds-user-role">{userRole}</div>
        </div>
        <button className="ds-icon-btn" onClick={onLogout} title="Cerrar sesión">
          <Icon name="logout" size={16} />
        </button>
      </div>
    </aside>
  )
}

interface LayoutRedesignProps {
  children: React.ReactNode
}

export default function LayoutRedesign({ children }: PropsWithChildren<LayoutRedesignProps>): JSX.Element {
  const history = useHistory()
  const location = useLocation()
  const { userData, logout } = useContext(AuthContext)
  const [drawerOpen, setDrawerOpen] = useState(true)

  const pathKey = location.pathname.split('/')[1] || 'citas'

  const handleLogout = async () => {
    const result = await Swal.fire(logoutSwalConfig('#1E6FD9'))
    if (result.isConfirmed) {
      await logout()
      history.push('/')
    }
  }

  const role = userData?.role?.id || ''
  const userName = userData?.name || userData?.email || ''
  const userRoleLabel = userData?.role?.name || role

  const visibleGeneral = NAV_GENERAL.filter((l) => l.roles.includes(role))
  const visibleRrhh = NAV_RRHH.filter((l) => l.roles.includes(role))

  const navigateTo = (link: string) => {
    history.push(link)
    setDrawerOpen(false)
  }

  return (
    <div className="ds-root">

      {/* ── Mobile top bar ── */}
      <header className="ds-mob-topbar">
        <button className="ds-mob-hamburger" onClick={() => setDrawerOpen(true)} aria-label="Abrir menú">
          <Icon name="menu" size={20} />
        </button>
        <div className="ds-logo">
          <MetaMark className="ds-logo-mark" />
          METAPHYSIO
        </div>
        <div className="ds-avatar" style={{ width: 32, height: 32, fontSize: 12 }}>{getInitials(userName)}</div>
      </header>

      {/* ── Mobile overlay + drawer ── */}
      <div className={`ds-mob-overlay${drawerOpen ? ' open' : ''}`} onClick={() => setDrawerOpen(false)} />
      <nav className={`ds-mob-drawer${drawerOpen ? ' open' : ''}`}>
        <div className="ds-sidebar-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div className="ds-logo">
            <MetaMark className="ds-logo-mark" />
            METAPHYSIO
          </div>
          <button className="ds-mob-hamburger" onClick={() => setDrawerOpen(false)} aria-label="Cerrar menú">
            <Icon name="close" size={18} />
          </button>
        </div>

        <div style={{ flex: 1, overflowY: 'auto' }}>
          <div className="ds-nav-section">
            <div className="ds-nav-title">General</div>
            {visibleGeneral.map((item) => (
              <div key={item.key} className={`ds-nav-item${pathKey === item.key ? ' ds-active' : ''}`} onClick={() => navigateTo(item.link)}>
                <span className="ds-nav-ico"><Icon name={item.icon} size={17} /></span>
                <span>{item.text}</span>
              </div>
            ))}
          </div>
          {visibleRrhh.length > 0 && (
            <div className="ds-nav-section">
              <div className="ds-nav-title">RRHH</div>
              {visibleRrhh.map((item) => (
                <div key={item.key} className={`ds-nav-item${pathKey === item.key ? ' ds-active' : ''}`} onClick={() => navigateTo(item.link)}>
                  <span className="ds-nav-ico"><Icon name={item.icon} size={17} /></span>
                  <span>{item.text}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="ds-sidebar-footer">
          <div className="ds-avatar">{getInitials(userName)}</div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="ds-user-name" style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{userName}</div>
            <div className="ds-user-role">{userRoleLabel}</div>
          </div>
          <button className="ds-icon-btn" onClick={handleLogout} title="Cerrar sesión">
            <Icon name="logout" size={16} />
          </button>
        </div>
      </nav>

      <div className="ds-app">
        <Sidebar activeKey={pathKey} role={role} userName={userName} userRole={userRoleLabel} onLogout={handleLogout} />
        <div className="ds-main">
          {children}
        </div>
      </div>
      {/* De momento el asistente por Google Chat solo está abierto a role1, para probarlo */}
      {role === 'role1' && <AssistantChatButton />}
    </div>
  )
}
